import { Helmet } from 'react-helmet-async';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  approveLocation,
  approveRoute,
  getLocation,
  getRoute,
  listAllLocationMedia,
  listAllRouteMedia,
  rejectLocation,
  rejectRoute,
  type LocationItem,
  type LocationMedia,
  type RouteItem,
  type RouteMediaItem,
} from '../lib/api';
import { renderContentBlocksHtml, extractTextFromContentBlocks } from '../lib/contentBlocks';
import { renderLocationSidebar } from '../templates/locationSidebar';

type ModerationModule = 'locations' | 'routes';

type ParsedModerationId = {
  module: ModerationModule;
  id: number;
};

const parseModerationId = (value?: string): ParsedModerationId | null => {
  if (!value) return null;
  const m = value.match(/^(locations|routes)-(\d+)$/);
  if (!m) return null;
  return { module: m[1] as ModerationModule, id: Number(m[2]) };
};

const statusLabel = (value?: string): string =>
  value ? value.split('_').join(' ').replace(/\b\w/g, (s: string) => s.toUpperCase()) : '-';

const formatDate = (value?: string | null): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-tight text-slate-500">{label}</p>
      <p className="mt-0.5 font-medium text-slate-800">{value}</p>
    </div>
  );
}

function LocationDetail({ entity, media }: { entity: LocationItem; media: LocationMedia[] }) {
  const descriptionHtml = renderContentBlocksHtml(entity.content_blocks)
    || (entity.description
      ? entity.description.split('\n').filter((p) => p.trim()).map((p) => `<p>${p}</p>`).join('')
      : extractTextFromContentBlocks(entity.content_blocks)
        ? `<p>${extractTextFromContentBlocks(entity.content_blocks)}</p>`
        : '<p class="text-slate-400 italic">No description provided.</p>');

  const heroMedia = media[0];
  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {heroMedia ? (
          <img src={heroMedia.file_large_url || heroMedia.file_url} alt={entity.title} className="aspect-video w-full object-cover" />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center bg-slate-100 text-slate-400 text-sm">No media</div>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">Description</h3>
        <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-400">Location Details</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <Field label="ID" value={`#${entity.id}`} />
          <Field label="Status" value={statusLabel(entity.status)} />
          <Field label="Title" value={entity.title} />
          <Field label="Location Name" value={entity.location_name || '-'} />
          <Field label="Coordinates" value={`${entity.latitude}°, ${entity.longitude}°`} />
          <Field label="Source" value={entity.source || '-'} />
          <Field label="Submitter" value={entity.submitter_display || entity.submitter_username || '-'} />
          <Field label="Featured" value={entity.is_featured ? 'Yes' : 'No'} />
          <Field label="Rating Avg" value={entity.rating_avg} />
          <Field label="Reviews" value={String(entity.reviews_count)} />
          <Field label="Favorites" value={String(entity.favorites_count)} />
          <Field label="Created At" value={formatDate(entity.created_at)} />
        </div>
      </section>
    </div>
  );
}

function RouteDetail({ entity, media }: { entity: RouteItem; media: RouteMediaItem[] }) {
  const descriptionHtml = renderContentBlocksHtml(entity.content_blocks)
    || (entity.description
      ? entity.description.split('\n').filter((p) => p.trim()).map((p) => `<p>${p}</p>`).join('')
      : extractTextFromContentBlocks(entity.content_blocks)
        ? `<p>${extractTextFromContentBlocks(entity.content_blocks)}</p>`
        : '<p class="text-slate-400 italic">No description provided.</p>');
  const heroMedia = media[0];

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {heroMedia ? (
          <img src={heroMedia.file_large_url || heroMedia.file_url} alt={entity.title} className="aspect-video w-full object-cover" />
        ) : entity.cover_image ? (
          <img src={entity.cover_image} alt={entity.title} className="aspect-video w-full object-cover" />
        ) : (
          <div className="flex aspect-video w-full items-center justify-center bg-slate-100 text-slate-400 text-sm">No media</div>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">Description</h3>
        <div className="prose prose-sm max-w-none text-slate-700" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-400">Route Details</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <Field label="ID" value={`#${entity.id}`} />
          <Field label="Status" value={statusLabel(entity.status)} />
          <Field label="Title" value={entity.title} />
          <Field label="Location Name" value={entity.location_name || '-'} />
          <Field label="Distance" value={entity.distance_km ? `${entity.distance_km} km` : '-'} />
          <Field label="Duration" value={entity.duration_minutes ? `${entity.duration_minutes} min` : '-'} />
          <Field label="Waypoints" value={String(entity.waypoints_count)} />
          <Field label="Favorites" value={String(entity.favorites_count)} />
          <Field label="Source" value={entity.source || '-'} />
          <Field label="Featured" value={entity.is_featured ? 'Yes' : 'No'} />
          <Field label="Start" value={entity.start_latitude && entity.start_longitude ? `${entity.start_latitude}, ${entity.start_longitude}` : '-'} />
          <Field label="End" value={entity.end_latitude && entity.end_longitude ? `${entity.end_latitude}, ${entity.end_longitude}` : '-'} />
          <Field label="Created At" value={formatDate(entity.created_at)} />
          <Field label="Updated At" value={formatDate(entity.updated_at)} />
        </div>
      </section>
    </div>
  );
}

export default function ModerationItemPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sidebar = useMemo(() => renderLocationSidebar('moderation'), []);
  const parsed = parseModerationId(id);
  const moderationReturnUrl = `/moderation/${id || ''}`;
  const fullEditorUrl = parsed
    ? parsed.module === 'locations'
      ? `/locations/create?id=${parsed.id}&return=${encodeURIComponent(moderationReturnUrl)}`
      : `/routes/create?id=${parsed.id}&return=${encodeURIComponent(moderationReturnUrl)}`
    : '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busyAction, setBusyAction] = useState<'approve' | 'reject' | null>(null);
  const [location, setLocation] = useState<LocationItem | null>(null);
  const [route, setRoute] = useState<RouteItem | null>(null);
  const [locationMedia, setLocationMedia] = useState<LocationMedia[]>([]);
  const [routeMedia, setRouteMedia] = useState<RouteMediaItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!parsed) {
        setError('Invalid moderation item id.');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        setLocation(null);
        setRoute(null);
        setLocationMedia([]);
        setRouteMedia([]);

        if (parsed.module === 'locations') {
          const [item, media] = await Promise.all([getLocation(parsed.id), listAllLocationMedia(parsed.id)]);
          if (!cancelled) {
            setLocation(item);
            setLocationMedia(media.results);
          }
        } else {
          const [item, media] = await Promise.all([getRoute(parsed.id), listAllRouteMedia(parsed.id)]);
          if (!cancelled) {
            setRoute(item);
            setRouteMedia(media.results);
          }
        }
      } catch {
        if (!cancelled) setError('Failed to load moderation item.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [parsed?.id, parsed?.module]);

  const doAction = async (action: 'approve' | 'reject') => {
    if (!parsed) return;
    try {
      setBusyAction(action);
      if (parsed.module === 'locations') {
        if (action === 'approve') await approveLocation(parsed.id, note);
        else await rejectLocation(parsed.id, note);
      } else {
        if (action === 'approve') await approveRoute(parsed.id, note);
        else await rejectRoute(parsed.id, note);
      }
      navigate('/moderation');
    } catch {
      setError(`Failed to ${action} item.`);
    } finally {
      setBusyAction(null);
    }
  };

  const title = location?.title || route?.title || '-';
  const moduleLabel = parsed?.module === 'locations' ? 'Locations' : 'Routes';
  const status = statusLabel(location?.status || route?.status);

  return (
    <>
      <Helmet>
        <title>MotoJourney | Moderation Item</title>
      </Helmet>
      <div className="flex min-h-screen bg-background-light">
        <div dangerouslySetInnerHTML={{ __html: sidebar }} />
        <main className="ml-64 flex-1 pb-20">
          <header className="px-8 pt-6">
            <nav className="mb-4 flex items-center gap-2 text-sm text-slate-500">
              <a href="/moderation" className="hover:text-primary">Moderation</a>
              <span className="material-symbols-outlined text-sm">chevron_right</span>
              <span className="font-medium text-slate-900">Item #{id || '-'}</span>
            </nav>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{title}</h1>
              <div className="flex flex-wrap items-center gap-2">
                {parsed ? (
                  <a
                    href={fullEditorUrl}
                    className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    <span className="material-symbols-outlined text-sm">edit</span>
                    Open Full Editor
                  </a>
                ) : null}
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                  {moduleLabel}
                </span>
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {status}
                </span>
              </div>
            </div>
          </header>

          {loading ? (
            <div className="px-8 py-6">
              <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">Loading...</div>
            </div>
          ) : error ? (
            <div className="px-8 py-6">
              <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-700">{error}</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 px-8 py-8 xl:grid-cols-3">
              <section className="space-y-6 xl:col-span-2">
                {location ? <LocationDetail entity={location} media={locationMedia} /> : null}
                {route ? <RouteDetail entity={route} media={routeMedia} /> : null}

                <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="mb-4 text-lg font-bold text-slate-900">Moderator Decision</h3>
                  {parsed ? (
                    <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      Need full editing with AI/Gemini before approve? Use the <strong>Open Full Editor</strong> button above.
                    </p>
                  ) : null}
                  <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="comment">
                    Internal Note / Feedback for User
                  </label>
                  <textarea
                    id="comment"
                    rows={4}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    className="block w-full rounded-lg border-slate-200 bg-slate-50 text-sm focus:border-primary focus:ring-primary"
                    placeholder="Add a note explaining your decision..."
                  />
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button
                      onClick={() => doAction('approve')}
                      disabled={busyAction !== null}
                      className="inline-flex flex-1 items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-60"
                    >
                      {busyAction === 'approve' ? 'Approving...' : 'Approve'}
                    </button>
                    <button
                      onClick={() => doAction('reject')}
                      disabled={busyAction !== null}
                      className="inline-flex flex-1 items-center justify-center rounded-lg border border-red-100 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-100 disabled:opacity-60"
                    >
                      {busyAction === 'reject' ? 'Rejecting...' : 'Reject'}
                    </button>
                  </div>
                </section>
              </section>

              <aside className="space-y-6">
                <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 px-6 py-4">
                    <h3 className="font-bold text-slate-900">Submission Info</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-4 p-6 text-sm">
                    <Field label="Module" value={moduleLabel} />
                    <Field label="Status" value={status} />
                    <Field label="Item ID" value={`#${parsed?.id ?? '-'}`} />
                    <Field label="Updated At" value={formatDate(location?.updated_at || route?.updated_at)} />
                  </div>
                </section>
              </aside>
            </div>
          )}
        </main>
      </div>
    </>
  );
}
